import { useCustomerAuth, useRestaurantInfo, type PricedProduct } from '@agos/customer-core'
import { retryTransient } from '@agos/api-client'
import { useGetCustomerAuthMeMostOrdered } from '@agos/api-client/customers'
import { T, formatCents, formatCount, formatDecimal, tt } from '@agos/shared'
import { FacebookIcon, GoogleIcon, InstagramIcon, TiktokIcon, cn } from '@agos/ui'
import {
  Clock,
  Eye,
  ExternalLink,
  Flame,
  Gift,
  Mail,
  MapPin,
  Phone,
  Plus,
  Star,
  Tag,
  Ticket,
  UtensilsCrossed,
} from 'lucide-react'

import {
  aboutUsOf,
  bannerOf,
  guaranteesOf,
  promoBannerOf,
  missingPickedNote,
  mostOrderedOf,
  newsletterOf,
  pickedProductsOf,
  resolvePicked,
  socialLinksOf,
  testimonialsOf,
  virtualTourOf,
} from './module-content'
import { NewsletterForm } from './NewsletterForm'
import { PersonalizedGreeting } from './PersonalizedGreeting'
import { baseModuleKey, type HomeModule } from './modules'

/**
 * The twelve homepage sections, drawn from what the restaurant configured.
 *
 * The layouts are legacy's, section by section — the black info cards, the red guarantee blocks, the
 * branded social buttons, the newsletter card with its offset green disc. The first AGOS draft drew each
 * one as a bordered box with a heading, which is why the owner's inspection said the design does not
 * match; every field was right and every screen was wrong.
 *
 * Every field is optional and admin-authored, so the readers in `module-content.ts` answer defaults and
 * this file only lays them out. Two of them carry real risk and are handled there rather than here:
 *
 * - **`virtual_tour`** is an admin-supplied URL in an iframe. It is drawn only for a host on legacy's
 *   allow-list, over HTTPS. Without the list, whoever holds the CMS password can put any page on the
 *   customer's homepage.
 * - **`about_us.linkUrl`** is an admin-authored href, so it is `http(s)`-or-relative only. A
 *   `javascript:` URL there would run in the customer's session.
 *
 * A key this build cannot draw is reported by `unknownNote`, not silently skipped — FB-166's lesson.
 */

function Section(props: { module: HomeModule; className?: string; children: React.ReactNode }) {
  return (
    <section data-home-module={props.module.key} className={props.className ?? ''}>
      {props.children}
    </section>
  )
}

/**
 * Legacy's product-row heading: a coloured icon tile, then the title, left-aligned.
 *
 * The centred `Heading` below is right for the banner and the newsletter and wrong here — FB-C51. It
 * also loses the tile, which is the only thing distinguishing *Obľúbené jedlá* (a flame) from
 * *Odporúčania šéfkuchára* (crossed utensils) at a glance.
 */
function RowHeading(props: { title: string; icon: typeof Flame; tone: 'orange' | 'green' }) {
  const Icon = props.icon
  const tile = props.tone === 'orange' ? 'bg-orange-500/10' : 'bg-green-600/10'
  const ink = props.tone === 'orange' ? 'text-orange-500' : 'text-green-600'
  return (
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl ${tile} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${ink}`} />
      </div>
      <h2 className="font-display font-bold text-xl text-foreground" data-module-title>
        {props.title}
      </h2>
    </div>
  )
}

function Heading(props: { title: string | null; subtitle?: string | null }) {
  if ((props.title === null || props.title === '') && (props.subtitle ?? '') === '') return null
  return (
    <div className="text-center mb-5">
      {props.title !== null && props.title !== '' && (
        <h2 className="text-xl sm:text-2xl font-black text-foreground mb-1" data-module-title>
          {props.title}
        </h2>
      )}
      {props.subtitle !== null && props.subtitle !== undefined && props.subtitle !== '' && (
        <p className="text-sm text-muted-foreground">{props.subtitle}</p>
      )}
    </div>
  )
}

/** The carousel legacy uses for a picked set of products — chef's picks, popular, most ordered. */
/** The price a card shows, in cents. The API sends a number or a decimal string. */
function priceCentsOf(product: PricedProduct): number {
  return typeof product.price === 'number'
    ? Math.round(product.price * 100)
    : Math.round(Number(product.price) * 100) || 0
}

/**
 * The menu's own badge, on a module tile — §9.234.
 *
 * §9.155's argument, one screen over: a tile that is merely faint reads as a rendering glitch rather
 * than as a decision somebody made, so the state is written down as well as drawn.
 */
function UnavailableBadge() {
  return (
    <div
      data-unavailable-badge
      className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full bg-zinc-900/85 text-white text-[10px] font-bold uppercase tracking-wide"
    >
      <T>Nedostupné</T>
    </div>
  )
}

/**
 * The green square with a plus, identical on both rows.
 *
 * **§9.233 — two different refusals, and only one was here.** `mayOrder` is the *opening hours* gate.
 * A product switched off with *Dostupný na objednanie* is a different question, and these rows call
 * `add({ productId })` straight from this button without ever reaching `addProduct`'s guard — so the
 * home page sold what the menu refuses. Checked here because all four row layouts share this button
 * and four copies of a rule is how one of them goes missing.
 *
 * **`!== false`** keeps selling anything whose payload predates the field, as `addProduct` does.
 */
function AddButton(props: {
  productId: number
  onAdd: (productId: number) => void
  mayOrder: boolean
  orderable?: boolean
}) {
  const unavailable = props.orderable === false
  return (
    <button
      type="button"
      data-module-add={props.productId}
      data-module-unavailable={unavailable ? 'yes' : undefined}
      // Said, not merely greyed: §9.155's own argument is that a faint control reads as a rendering
      // glitch rather than as a decision somebody made.
      aria-label={unavailable ? tt('Momentálne nedostupné') : tt('Do košíka')}
      title={unavailable ? tt('Momentálne nedostupné') : undefined}
      disabled={!props.mayOrder || unavailable}
      onClick={() => props.onAdd(props.productId)}
      className="w-11 h-11 rounded-lg bg-green-600 hover:bg-green-700 text-white flex items-center justify-center active:scale-95 transition-all disabled:opacity-50"
    >
      <Plus className="w-4 h-4" />
    </button>
  )
}

/** A product with no picture. Legacy draws the section's own icon, faintly, rather than a gap. */
function NoImage(props: { icon: typeof Flame; className: string }) {
  const Icon = props.icon
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/80">
      <Icon className={props.className} />
    </div>
  )
}

/**
 * The products a row shows.
 *
 * Legacy falls back to the **first six** (popular) or **first four** (chef) when the restaurant has
 * configured no ids at all. This port returned nothing in that case, so a franchise that switched the
 * section on without picking dishes got an empty page where legacy shows a selection. It does not bite
 * on this data — both sections name their ids — which is exactly why it needed reading rather than
 * looking at.
 */
function rowProducts(
  module: HomeModule,
  products: readonly PricedProduct[],
  fallbackTitle: string,
  fallbackCount: number,
) {
  const picked = pickedProductsOf(module, fallbackTitle)
  if (picked.ids.length === 0) {
    return { title: picked.title, found: products.slice(0, fallbackCount), missing: [] as number[] }
  }
  const { found, missing } = resolvePicked(picked.ids, products)
  return { title: picked.title, found, missing }
}

function MissingNote(props: { missing: readonly number[] }) {
  // Legacy skips a withdrawn product silently. A carousel that quietly shows three of four looks like a
  // carousel of three, and the restaurant has no way to notice its choice stopped being honoured.
  return props.missing.length === 0 ? null : (
    <p className="text-[11px] text-amber-500" data-module-missing>
      {missingPickedNote([...props.missing])}
    </p>
  )
}

/**
 * *Obľúbené jedlá* — a grid, with the top three numbered.
 *
 * Legacy's own layout: two columns on a phone, three from `sm`, a 128-pixel image, and an orange rank
 * badge on the first three cards. This port drew one generic four-column row for both product sections
 * with no badge and no icon, so the two were indistinguishable and the ranking was invisible — FB-C51.
 */
function PopularDishes(props: {
  module: HomeModule
  products: readonly PricedProduct[]
  onAdd: (productId: number) => void
  mayOrder: boolean
}) {
  const row = rowProducts(props.module, props.products, tt('Obľúbené jedlá'), 6)
  if (row.found.length === 0 && row.missing.length === 0) return null

  return (
    <Section module={props.module} className="space-y-4">
      <RowHeading title={row.title} icon={Flame} tone="orange" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-module-products>
        {row.found.map((product, index) => {
          const imageUrl = (product as { imageUrl?: string | null }).imageUrl ?? null
          return (
            <div
              key={product.id}
              data-module-product={product.id}
              className={cn(
                'bg-card border border-border rounded-2xl overflow-hidden group hover:border-green-600/30 hover:shadow-md transition-all',
                // §9.234 — the menu's greying, without its `pointer-events-none`: the disabled
                // button is the block here, and an inert wrapper would make it unreachable.
                (product as { orderable?: boolean }).orderable === false && 'opacity-50 grayscale',
              )}
            >
              <div className="relative h-32 bg-muted overflow-hidden">
                {(product as { orderable?: boolean }).orderable === false && <UnavailableBadge />}
                {imageUrl !== null && imageUrl !== '' ? (
                  <img
                    src={imageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover md:group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <NoImage icon={Flame} className="w-8 h-8 text-muted-foreground/20" />
                )}
                {index < 3 && (
                  <div
                    data-module-rank={index + 1}
                    className="absolute top-2 left-2 w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center"
                  >
                    {formatCount(index + 1)}
                  </div>
                )}
              </div>
              <div className="p-3 space-y-2 bg-card">
                <h3 className="font-display font-bold text-sm text-foreground line-clamp-2 leading-tight">
                  {product.name}
                </h3>
                <div className="flex items-center justify-between">
                  <span className="font-extrabold text-foreground text-lg">
                    {formatCents(priceCentsOf(product))}
                  </span>
                  <AddButton
                    productId={product.id}
                    onAdd={props.onAdd}
                    mayOrder={props.mayOrder}
                    orderable={(product as { orderable?: boolean }).orderable}
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <MissingNote missing={row.missing} />
    </Section>
  )
}

/**
 * *Odporúčania šéfkuchára* — a row that scrolls sideways, and shows what the dish is.
 *
 * Legacy's cards are a fixed width in a snapping horizontal scroller and carry the **description**,
 * which is the difference between *Chicken bowl* and *Ľadový šalát, kuracie prsia 150g, domáce
 * hranolky, Cheddar, Bbq…*. This port dropped both.
 */
function ChefRecommendations(props: {
  module: HomeModule
  products: readonly PricedProduct[]
  onAdd: (productId: number) => void
  mayOrder: boolean
}) {
  const row = rowProducts(props.module, props.products, tt('Odporúčania šéfkuchára'), 4)
  if (row.found.length === 0 && row.missing.length === 0) return null

  return (
    <Section module={props.module} className="space-y-4">
      <RowHeading title={row.title} icon={UtensilsCrossed} tone="green" />
      <div
        className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
        data-module-products
      >
        {row.found.map((product) => {
          const imageUrl = (product as { imageUrl?: string | null }).imageUrl ?? null
          const description = (product as { description?: string | null }).description ?? null
          return (
            <div
              key={product.id}
              data-module-product={product.id}
              className={cn(
                'snap-start flex-shrink-0 w-52 sm:w-60 bg-card border border-border rounded-2xl overflow-hidden group hover:border-green-600/30 hover:shadow-md transition-all',
                (product as { orderable?: boolean }).orderable === false && 'opacity-50 grayscale',
              )}
            >
              <div className="relative h-36 bg-muted overflow-hidden">
                {(product as { orderable?: boolean }).orderable === false && <UnavailableBadge />}
                {imageUrl !== null && imageUrl !== '' ? (
                  <img
                    src={imageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover md:group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <NoImage icon={UtensilsCrossed} className="w-10 h-10 text-muted-foreground/30" />
                )}
              </div>
              <div className="p-3 space-y-2 bg-card">
                <h3 className="font-display font-bold text-sm text-foreground line-clamp-2 leading-tight">
                  {product.name}
                </h3>
                {description !== null && description !== '' && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{description}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="font-extrabold text-foreground text-lg">
                    {formatCents(priceCentsOf(product))}
                  </span>
                  <AddButton
                    productId={product.id}
                    onAdd={props.onAdd}
                    mayOrder={props.mayOrder}
                    orderable={(product as { orderable?: boolean }).orderable}
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <MissingNote missing={row.missing} />
    </Section>
  )
}

/**
 * *Vaše najčastejšie* — what this customer orders, for a one-tap re-add.
 *
 * Legacy hides it completely for a guest and when the list comes back empty: `if (!user ||
 * items.length === 0) return null`. This port drew the heading regardless, so every signed-out visitor
 * got a section title over empty space, which reads as a page that failed to load rather than as one
 * that is personal — FB-C51.
 *
 * `GET /customer-auth/me/most-ordered?limit=6`, legacy's own call and limit.
 */
function MostOrdered(props: {
  module: HomeModule
  onAdd: (productId: number) => void
  mayOrder: boolean
}) {
  const signedIn = useCustomerAuth((state) => state.user) !== null
  /* FB-835, §9.549 - a blip took the reorder shortcut away, and `return null` below means it goes silently. */
  const { data } = useGetCustomerAuthMeMostOrdered(
    { limit: 6 },
    { query: { enabled: signedIn, retry: retryTransient } },
  )
  const items = mostOrderedOf(data)
  if (!signedIn || items.length === 0) return null

  return (
    <Section module={props.module}>
      <div className="flex items-center gap-2 mb-3">
        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
        <h2 className="font-bold text-foreground text-base" data-module-title>
          <T>Vaše najčastejšie</T>
        </h2>
        <span className="text-xs text-muted-foreground">
          <T>- rýchle pridanie do košíka</T>
        </span>
      </div>
      <div className="-mx-4 px-4 overflow-x-auto">
        <div className="flex gap-3 pb-2 snap-x snap-mandatory" data-module-products>
          {items.map((item) => (
            <div
              key={item.id}
              data-module-product={item.id}
              className={cn(
                'relative snap-start shrink-0 w-44 sm:w-52 rounded-2xl border border-border bg-card overflow-hidden flex flex-col',
                (item as { orderable?: boolean }).orderable === false && 'opacity-50 grayscale',
              )}
            >
              {(item as { orderable?: boolean }).orderable === false && <UnavailableBadge />}
              {item.imageUrl !== null ? (
                <div className="aspect-[4/3] bg-muted overflow-hidden">
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                  <UtensilsCrossed className="w-8 h-8 text-primary/40" />
                </div>
              )}
              <div className="p-3 flex flex-col gap-2 flex-1">
                <div className="min-w-0 flex-1">
                  <p
                    className="text-sm font-bold text-foreground line-clamp-2 leading-tight"
                    title={item.name}
                  >
                    {item.name}
                  </p>
                  {item.orderedCount > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      <T>Objednané</T> {formatCount(item.orderedCount)}×
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2 mt-auto">
                  <span className="font-bold text-foreground text-sm">
                    {formatCents(item.priceCents)}
                  </span>
                  <AddButton
                    productId={item.id}
                    onAdd={props.onAdd}
                    mayOrder={props.mayOrder}
                    orderable={(item as { orderable?: boolean }).orderable}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}

/** One of the three black cards under the banner: an icon in an amber tile, a value, and its label. */
function InfoCard(props: {
  icon: typeof Clock
  value: string
  label: React.ReactNode
  href?: string
  testId: string
}) {
  const Icon = props.icon
  const body = (
    <>
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <p className="text-sm text-white font-bold leading-relaxed">{props.value}</p>
        <p className="text-xs font-bold text-white mt-1">{props.label}</p>
      </div>
    </>
  )
  if (props.href === undefined) {
    return (
      <div className="flex items-start gap-3 p-4 rounded-2xl bg-black" data-banner-info={props.testId}>
        {body}
      </div>
    )
  }
  return (
    <a
      href={props.href}
      data-banner-info={props.testId}
      {...(props.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="flex items-start gap-3 p-4 rounded-2xl bg-black transition-opacity hover:opacity-75 active:opacity-60"
    >
      {body}
    </a>
  )
}

export function HomeModuleSection(props: {
  module: HomeModule
  products: readonly PricedProduct[]
  mayOrder: boolean
  onAdd: (productId: number) => void
  /** Opens a category on the menu — the greeting's cards link to one. */
  onOpenCategory?: (categoryId: number | null) => void
  /** §9.776 - the categories the menu draws, which the greeting's cards are checked against. */
  menuCategoryIds: ReadonlySet<number>
}) {
  const { module } = props

  // §9.522, FB-802 - the type rather than the row: a second instance carries a `:N` suffix and found no case
  // here, so an operator enabling one saw nothing at all.
  switch (baseModuleKey(module.key)) {
    case 'banner': {
      const banner = bannerOf(module)
      const hasInfo =
        banner.hoursText !== null || banner.phoneText !== null || banner.addressText !== null
      return (
        <Section module={module} className="space-y-3">
          {banner.desktopImageUrl !== null && (
            <div className="relative w-full overflow-hidden rounded-2xl">
              <img
                src={banner.desktopImageUrl}
                alt=""
                loading="lazy"
                data-module-image
                className="w-full h-auto object-contain object-center max-h-[780px] mx-auto"
              />
            </div>
          )}
          {hasInfo && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {banner.hoursText !== null && (
                <InfoCard
                  icon={Clock}
                  testId="hours"
                  value={banner.hoursText}
                  label={<T>Prevádzková doba</T>}
                />
              )}
              {banner.phoneText !== null && (
                <InfoCard
                  icon={Phone}
                  testId="phone"
                  value={banner.phoneText}
                  href={`tel:${banner.phoneText.replace(/[^+\d]/g, '')}`}
                  label={<T>Telefonická objednávka</T>}
                />
              )}
              {banner.addressText !== null && (
                <InfoCard
                  icon={MapPin}
                  testId="address"
                  value={banner.addressText}
                  href={`https://maps.google.com/?q=${encodeURIComponent(banner.addressText)}`}
                  label={<T>Adresa</T>}
                />
              )}
            </div>
          )}
        </Section>
      )
    }

    case 'personalized_greeting':
      // `content` is empty on this data and legacy still renders two cards here, because the greeting
      // is not configured — it is `GET /customer-auth/personalized-suggestion`. This used to draw
      // *Dobrý deň, {name}* from a `greetingName` prop that `HomePage` passed as a literal `null`, so
      // nobody ever saw it and the sentence was invented besides. FB-C56.
      return (
        <Section module={module}>
          <PersonalizedGreeting
            products={props.products}
            menuCategoryIds={props.menuCategoryIds}
            mayOrder={props.mayOrder}
            onAdd={props.onAdd}
            onOpenCategory={props.onOpenCategory}
          />
        </Section>
      )

    case 'social_links':
      return <SocialLinks module={module} />

    case 'popular_dishes':
      return <PopularDishes {...props} />
    case 'chef_recommendations':
      return <ChefRecommendations {...props} />
    case 'most_ordered':
      // The list is the customer's own order history, so there is nothing to draw for a guest. Legacy
      // is explicit — `if (!user || items.length === 0) return null` — and this port drew the heading
      // anyway, so every signed-out visitor got *Najčastejšie objednávané* over empty space. A section
      // title with nothing under it reads as a page that failed to load, not as one that is personal.
      return <MostOrdered {...props} />



    case 'guarantees': {
      const blocks = guaranteesOf(module)
      return blocks.length === 0 ? null : (
        <Section module={module}>
          <div
            className={`grid grid-cols-1 gap-4 ${blocks.length === 2 ? 'sm:grid-cols-2' : blocks.length >= 3 ? 'sm:grid-cols-3' : ''}`}
            data-guarantees
          >
            {blocks.map((block, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center gap-2 p-5 rounded-2xl bg-red-600 border border-red-700 hover:border-red-400 hover:shadow-md transition-all"
              >
                <p className="font-extrabold text-base text-white uppercase tracking-wide">
                  {block.title}
                </p>
                {block.text !== '' && (
                  <p className="text-sm text-white font-semibold leading-relaxed">{block.text}</p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )
    }

    case 'virtual_tour': {
      const tour = virtualTourOf(module)
      return (
        <Section module={module} className="space-y-2">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-primary" />
            <h2 className="font-display font-bold text-lg text-foreground" data-module-title>
              {tour.title}
            </h2>
          </div>
          {tour.subtitle !== null && tour.subtitle !== '' && (
            <p className="text-sm text-muted-foreground">{tour.subtitle}</p>
          )}
          {tour.url === null ? (
            /* A URL from a host that is not on the list is not framed, and the section says so rather
               than showing an empty box that reads as a broken page. */
            <p className="text-[11px] text-amber-500" data-tour-refused>
              <T>Prehliadku sa nepodarilo načítať.</T>
            </p>
          ) : (
            <>
              <div className="rounded-2xl overflow-hidden border border-border">
                <iframe
                  data-tour-frame
                  title={tour.title}
                  src={tour.url}
                  loading="lazy"
                  style={{ height: tour.heightMobile }}
                  className="w-full border-0 block"
                />
              </div>
              <a
                href={tour.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-end gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <T>Otvoriť na celú obrazovku</T>
                <ExternalLink className="w-3 h-3" />
              </a>
            </>
          )}
        </Section>
      )
    }

    case 'about_us': {
      const about = aboutUsOf(module)
      // Legacy: `if (!description && !imageUrl) return null`. A link on its own is not an *about us*
      // section — and this franchise has configured exactly that, so the port drew an empty card with a
      // *Viac o nás* link where legacy draws nothing at all (FB-C51).
      if (about.description === null && about.imageUrl === null) return null
      return (
        <Section
          module={module}
          className="rounded-2xl border border-border bg-card overflow-hidden grid grid-cols-1 md:grid-cols-2"
        >
          {about.imageUrl !== null && (
            <img src={about.imageUrl} alt="" data-module-image className="w-full h-full object-cover" />
          )}
          <div className="p-6 sm:p-8 space-y-3 flex flex-col justify-center">
            {about.description !== null && (
              <p className="text-sm text-muted-foreground leading-relaxed" data-about-description>
                {about.description}
              </p>
            )}
            {about.linkUrl !== null && (
              <a
                href={about.linkUrl}
                data-about-link
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline"
              >
                <T>Viac o nás</T>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </Section>
      )
    }

    case 'newsletter': {
      const newsletter = newsletterOf(module)
      return (
        <Section
          module={module}
          className="relative overflow-hidden rounded-2xl bg-card border border-border p-8 text-center"
        >
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-green-600/5 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
          <div className="relative space-y-4 max-w-md mx-auto">
            <div className="w-12 h-12 rounded-2xl bg-green-600/10 flex items-center justify-center mx-auto">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="font-display font-bold text-xl text-foreground" data-module-title>
                {newsletter.title}
              </h2>
              {newsletter.subtitle !== null && newsletter.subtitle !== '' && (
                <p className="text-sm text-muted-foreground mt-1">{newsletter.subtitle}</p>
              )}
            </div>
            <NewsletterForm newsletter={newsletter} />
          </div>
        </Section>
      )
    }

    case 'testimonials': {
      const items = testimonialsOf(module)
      return items.length === 0 ? null : (
        <Section module={module} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testimonials>
            {items.map((item, index) => (
              <figure
                key={index}
                className="rounded-2xl border border-border bg-card p-5 space-y-3 flex flex-col"
              >
                <blockquote className="text-sm text-muted-foreground leading-relaxed flex-1">
                  {item.text}
                </blockquote>
                <figcaption className="text-sm font-bold text-foreground">{item.author}</figcaption>
              </figure>
            ))}
          </div>
        </Section>
      )
    }

    case 'promo_banner':
      // §9.289 — the stub that drew only `offerTitle` is gone. Its reason was that nobody had ever
      // configured the block; the owner has now switched it on with all twenty-five fields set, and
      // legacy has the layout, so there is nothing left to invent.
      return <PromoBanner module={module} />

    default:
      // Unreachable: `renderable` filters to KNOWN_MODULES and `unrenderable` reports the rest.
      return null
  }
}

/**
 * The promo panel — §9.289, the owner's report.
 *
 * Legacy's `PromoBannerModule.tsx` structure and classes. The reading is `promoBannerOf`, which is
 * where the defaults and the one departure from legacy live; this is the drawing.
 */
function PromoBanner({ module }: { module: HomeModule }) {
  const promo = promoBannerOf(module)
  const { offer, loyalty } = promo
  // The tags belong to the loyalty row, not to the offer above it — the first draft gated them on
  // `offer.shown` and would have hidden them on any franchise that runs the programme without an offer.
  const tags = [
    loyalty.tag1 !== null ? { icon: Gift, text: loyalty.tag1 } : null,
    loyalty.tag2 !== null ? { icon: Ticket, text: loyalty.tag2 } : null,
  ].filter((tag): tag is { icon: typeof Gift; text: string } => tag !== null)

  return (
    <section
      className="relative w-full overflow-hidden rounded-2xl"
      style={{ background: promo.gradient }}
      data-promo-banner
    >
      <div className="relative z-10 px-5 sm:px-8 py-5 sm:py-6 space-y-4">
        {offer.shown && (
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5" data-promo-offer>
            <div className="flex-shrink-0 flex items-center justify-center w-20 h-20 rounded-full bg-white shadow-xl">
              <div className="text-center leading-none">
                <span
                  className="block text-2xl sm:text-3xl font-black tracking-tight"
                  style={{ color: offer.badgeColour }}
                >
                  {offer.badgeText}
                </span>
                <span
                  className="block text-[10px] font-extrabold uppercase tracking-wider mt-0.5"
                  style={{ color: offer.labelColour }}
                >
                  {offer.label}
                </span>
              </div>
            </div>
            <div className="flex-1 text-center sm:text-left">
              {offer.kicker !== null && (
                <p className="text-yellow-300 text-[11px] sm:text-xs font-black uppercase tracking-widest mb-0.5">
                  {offer.kicker}
                </p>
              )}
              {(offer.title !== null || offer.highlight !== null) && (
                <h2 className="text-white text-xl sm:text-2xl font-black leading-tight">
                  {offer.title}{' '}
                  {offer.highlight !== null && (
                    <span className="inline-block bg-yellow-400 text-black rounded-md px-2 py-0.5">
                      {offer.highlight}
                    </span>
                  )}
                </h2>
              )}
              {offer.subtitle !== null && (
                <p className="mt-1 text-white text-xs sm:text-sm font-bold">{offer.subtitle}</p>
              )}
            </div>
            {offer.sideBadge !== null && (
              <span className="flex-shrink-0 inline-block px-4 py-2 rounded-lg font-black text-xs sm:text-sm uppercase tracking-wide shadow-lg bg-yellow-400 text-black">
                {offer.sideBadge}
              </span>
            )}
          </div>
        )}

        {offer.shown && loyalty.shown && <div className="w-full h-px bg-white/20" />}

        {loyalty.shown && (
          <div className="flex items-center gap-3 sm:gap-4" data-promo-loyalty>
            <div
              className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center shadow"
              style={{ background: 'linear-gradient(135deg, #f1c40f, #e67e22)' }}
            >
              <Star className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              {loyalty.title !== null && (
                <p className="text-white text-sm sm:text-base font-black leading-tight">{loyalty.title}</p>
              )}
              {loyalty.subtitle !== null && (
                <p className="text-white text-xs font-semibold">{loyalty.subtitle}</p>
              )}
            </div>
            <div className="hidden sm:flex items-center gap-2">
              {tags.map((tag) => (
                <span
                  key={tag.text}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 text-white text-xs font-bold"
                >
                  <tag.icon className="w-3.5 h-3.5" /> {tag.text}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Legacy draws the tags twice - `hidden sm:flex` beside the title and `flex sm:hidden`
          * beneath it — because they do not fit next to the text on a phone. Keeping only the first
          * would have dropped them entirely on the width the owner actually uses. */}
        {loyalty.shown && tags.length > 0 && (
          <div className="flex sm:hidden gap-2" data-promo-tags-mobile>
            {tags.map((tag) => (
              <span
                key={tag.text}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 text-white text-xs font-bold flex-1 justify-center"
              >
                <tag.icon className="w-3.5 h-3.5" /> {tag.text}
              </span>
            ))}
          </div>
        )}

        {promo.pointsCard.shown && promo.pointsCard.text !== null && (
          <div
            className="flex items-center gap-3 p-3.5 rounded-xl bg-white/15 border border-yellow-400/40"
            data-promo-points
          >
            <div
              className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #9b59b6, #8e44ad)' }}
            >
              <Tag className="w-4 h-4 text-white" />
            </div>
            <p className="text-white text-sm font-black flex-1">{promo.pointsCard.text}</p>
          </div>
        )}

        {promo.emailHint.shown && promo.emailHint.text !== null && (
          <div className="flex items-start gap-3 p-3 rounded-xl bg-black/20" data-promo-email-hint>
            <Mail className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />
            <p className="text-white text-[11px] sm:text-xs leading-relaxed font-medium">
              {promo.emailHint.text}
            </p>
          </div>
        )}

        {promo.finalNote !== null && (
          <p className="text-center text-yellow-300 text-[10px] font-black uppercase tracking-wider">
            {promo.finalNote}
          </p>
        )}
      </div>
    </section>
  )
}

/**
 * *Sledujte nás* — the Google rating and the three branded buttons.
 *
 * The links come from `/settings/footer` and not from the module's own content, which is legacy's
 * arrangement: a restaurant sets its socials once, in settings, and the module decides whether to show
 * them. Every one has already been through `safeLink`.
 */
function SocialLinks({ module }: { module: HomeModule }) {
  const social = socialLinksOf(module)
  const info = useRestaurantInfo()
  // 4.4 is legacy's own default when the franchise configured none.
  const rating = social.googleRating ?? 4.4

  const any =
    info.facebookUrl !== null ||
    info.instagramUrl !== null ||
    info.tiktokUrl !== null ||
    info.googleReviewsUrl !== null
  if (!any) return null

  return (
    <Section
      module={module}
      className="relative w-full overflow-hidden rounded-2xl border border-border bg-card p-6 sm:p-8"
    >
      <Heading title={social.title} subtitle={social.subtitle} />
      <div className="flex flex-wrap items-stretch justify-center gap-3">
        {info.googleReviewsUrl !== null && (
          <a
            href={info.googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-social="google"
            className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-white text-zinc-900 border border-zinc-200 hover:shadow-md transition-all"
          >
            <GoogleIcon className="w-6 h-6" />
            <div className="flex flex-col items-start">
              <span className="text-xs text-zinc-500 font-semibold">Google</span>
              <div className="flex items-center gap-2">
                <Stars rating={rating} />
                {/* `4,4`, not `4.4` - the rating sits beside prices that already carry a comma. */}
                <span className="text-xs font-bold text-zinc-900" data-google-rating>
                  {formatDecimal(rating, 1)}
                </span>
              </div>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        )}
        {info.facebookUrl !== null && (
          <a
            href={info.facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-social="facebook"
            className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#1877F2] text-white hover:opacity-90 transition-opacity"
          >
            <FacebookIcon className="w-5 h-5" />
            <span className="text-sm font-bold">Facebook</span>
          </a>
        )}
        {info.instagramUrl !== null && (
          <a
            href={info.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-social="instagram"
            className="flex items-center gap-2 px-4 py-3 rounded-xl text-white hover:opacity-90 transition-opacity"
            style={{
              background:
                'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
            }}
          >
            <InstagramIcon className="w-5 h-5" />
            <span className="text-sm font-bold">Instagram</span>
          </a>
        )}
        {info.tiktokUrl !== null && (
          <a
            href={info.tiktokUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-social="tiktok"
            className="flex items-center gap-2 px-4 py-3 rounded-xl bg-black text-white hover:opacity-90 transition-opacity"
          >
            <TiktokIcon className="w-5 h-5" />
            <span className="text-sm font-bold">TikTok</span>
          </a>
        )}
      </div>
    </Section>
  )
}

/** Five stars, part-filled by a gradient stop — legacy's own, because 4.4 is not four stars. */
function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((index) => {
        const percent = Math.round(Math.min(1, Math.max(0, rating - (index - 1))) * 100)
        const id = `social-star-${index}`
        return (
          <svg key={index} viewBox="0 0 20 20" className="w-3.5 h-3.5" aria-hidden="true">
            <defs>
              <linearGradient id={id}>
                <stop offset={`${percent}%`} stopColor="#FBBC05" />
                <stop offset={`${percent}%`} stopColor="#D1D5DB" />
              </linearGradient>
            </defs>
            <path
              fill={`url(#${id})`}
              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
            />
          </svg>
        )
      })}
    </div>
  )
}
